// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import ReactSelectBase, { components } from 'react-select';
import ReactSelectAsyncCreatable from 'react-select/async-creatable';
import {
  SortableContainer,
  SortableElement,
  sortableHandle
} from 'react-sortable-hoc';
import SelectInput from 'components/SelectInput';
import Box from 'components/Box';
import Icon from 'components/Icon';
import ClientOnly from 'containers/ClientOnly';
import { ChevronUpIcon, ChevronDownIcon } from 'components/ChevronIcon';
import { getter, compose } from 'utils/functional';
import { colors, transition } from '../theme';

const DropdownIndicator = (props: { menuIsOpen: boolean }) => (
  <components.DropdownIndicator {...props}>
    {props.menuIsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
  </components.DropdownIndicator>
);

const ClearIndicator = props => (
  <components.ClearIndicator {...props}>
    <Icon name="close" fill="currentColor" width={12} height={12} />
  </components.ClearIndicator>
);

const ReactSelect = ({ label, ...props }: { label?: React.Node }) => (
  <label>
    {label && <Box display="none">{label}</Box>}
    <ReactSelectBase {...props} />
  </label>
);

const MultiValueRemove = ({
  innerProps,
  ...otherProps
}: {
  innerProps: Object,
  ...
}) => (
  <components.MultiValueRemove
    innerProps={{ 'data-qa-id': 'react-select-remove', ...innerProps }}
    {...otherProps}
  />
);

export const StyledSelectInput = styled(SelectInput)`
  height: 50px;
  padding: 0 calc(1em - 2px);
  border-radius: 7px;
  border: 1px solid ${colors.aluminum};
  color: ${colors.waterBlue};
`;

const styles = css`
  display: block;
  width: 100%;
  min-height: 45px;
  vertical-align: middle;
  color: #484848;
  font-size: 18px;

  &.react-select--is-disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transition: opacity ${transition} 0.5s;
  }

  .react-select__control {
    background-color: white;
    width: 100%;
    min-height: 45px;
    margin: 0;
    cursor: pointer;
  }

  .react-select__control--menu-is-open {
    .react-select__dropdown-indicator {
      svg {
        transform: rotate(180deg);
      }
    }
  }

  .react-select__input {
    height: 100%;
    padding-left: 0;
  }

  .react-select__value-container {
    display: flex;
    padding: 0 1em;
    align-items: center;
  }
  .react-select__value-container--has-value.react-select__value-container--is-multi {
    margin-left: -6px;
    padding: 2px 1em;
  }

  .react-select__indicators {
    padding-top: 2px;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    padding-right: 1em;
  }

  .react-select__single-value {
    margin: 0;
    max-width: calc(100% - 15px);
  }

  .react-select__multi-value {
    background: ${colors.platinum};
    color: ${colors.oceanBlue};
  }
  .react-select__multi-value__label {
    color: inherit;
  }
  .react-select__multi-value__remove {
    border-radius: 0 2px 2px 0;
    transition: all ${transition};
    &:hover {
      background: ${colors.waterBlue};
      color: white;
    }
  }

  .react-select__menu {
    z-index: 5;
    margin-top: 1px;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.15);
    border: solid 1px #c4c8cc;
  }

  .react-select__menu-notice {
    text-align: left;
  }

  .react-select__option {
    display: flex;
    align-items: center;
    border-bottom: solid 1px #c4c8cc;
    color: #484848;
    padding: 0 1em;
    height: 45px;
    cursor: pointer;
    &:last-child {
      border-bottom: none;
    }
  }
  .react-select__option--is-selected {
    background-color: rgba(241, 241, 241, 1);
    color: inherit;
  }
  .react-select__option--is-focused {
    background-color: rgba(241, 241, 241, 1);
    color: inherit;
  }
`;

export const StyledSelect = styled(ReactSelect).attrs(
  ({ components: otherComponents }) => ({
    classNamePrefix: 'react-select',
    components: {
      DropdownIndicator,
      ClearIndicator,
      MultiValueRemove,
      ...otherComponents
    },
    maxMenuHeight: 250
  })
)`
  ${styles}
`;

const SortableMultiValue = SortableElement(props => {
  // this prevents the menu from being opened/closed when the user clicks
  // on a value to begin dragging it. ideally, detecting a click (instead of
  // a drag) would still focus the control and toggle the menu, but that
  // requires some magic with refs that are out of scope for this example
  const onMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  const innerProps = { ...props.innerProps, onMouseDown };
  return <components.MultiValue {...props} innerProps={innerProps} />;
});

const SortableMultiValueLabel = sortableHandle(props => (
  <components.MultiValueLabel {...props} />
));

const SortableSelect = SortableContainer(ReactSelect);

export const StyledSelectSortable = styled(SortableSelect).attrs(
  ({ components: otherComponents }) => ({
    classNamePrefix: 'react-select',

    useDragHandle: true,
    // react-sortable-hoc props:
    axis: 'xy',
    distance: 0, // NOTE: must be 0 for selenium tests to work
    // small fix for https://github.com/clauderic/react-sortable-hoc/pull/352:
    getHelperDimensions: ({ node }) => node.getBoundingClientRect(),

    components: {
      DropdownIndicator,
      ClearIndicator,
      MultiValueRemove,
      MultiValue: SortableMultiValue,
      MultiValueLabel: SortableMultiValueLabel,
      ...otherComponents
    }
  })
)`
  ${styles}
`;

export const AsyncCreatable = styled(ReactSelectAsyncCreatable).attrs(() => ({
  classNamePrefix: 'react-select',
  components: {
    DropdownIndicator,
    ClearIndicator,
    MultiValueRemove
  }
}))`
  ${styles}
`;

const Select = ({
  name,
  label,
  value,
  options,
  disabled,
  onChange,
  className,
  ...props
}: {
  name?: string,
  label?: React.Node,
  value: ?string,
  options: Array<{ value: string, label: string }>,
  disabled?: boolean,
  onChange?: Function,
  className?: string,
  'data-qa-id'?: string
}) => {
  const classNames = props['data-qa-id']
    ? [`qa-id-${props['data-qa-id']}`]
    : [];
  if (className) {
    classNames.push(className);
  }

  const Container = label ? 'label' : React.Fragment;

  return (
    <ClientOnly
      placeholder={
        <StyledSelectInput
          options={options}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          className={classNames.join(' ')}
        />
      }
    >
      <Container>
        {label && <Box display="none">{label}</Box>}
        <StyledSelect
          name={name}
          options={options}
          value={options.filter(({ value: v }) => v === value)}
          isDisabled={disabled}
          onChange={onChange}
          isSearchable={false}
          isClearable={false}
          className={classNames.join(' ')}
          {...props}
        />
      </Container>
    </ClientOnly>
  );
};

export default Select;

export const SearchableSelect = ({
  onChange,
  ...props
}: {
  name?: string,
  value: ?string,
  options: Array<{ value: string, label: string }>,
  disabled?: boolean,
  onChange?: Function,
  'data-qa-id'?: string
}) => (
  <Select
    onChange={compose(
      onChange,
      getter('value')
    )}
    {...props}
    isSearchable
  />
);
